"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FileUploader = void 0;
const React = require("react");
const react_1 = require("react");
const react_dropzone_1 = require("react-dropzone");
const _ = require("lodash");
const axios_1 = require("axios");
const CameraUploadContext_1 = require("../../../../contexts/CameraUploadContext");
const Button_1 = require("../Button");
const guid_1 = require("../../../../apis/spg/util/guid");
const styles = require("./styles.scss");
const cssComposer_1 = require("../../../../helpers/cssComposer");
const css = (0, cssComposer_1.default)(styles);
/**
 * This component acts as a dropzone and enables users to upload files.
 * The intent for this component is to act a base component in which others extend/utilise from.
 * This component does not hold any state, it just consumes a context, uploads, does some validation,
 * and renders the children passed into it.
 *
 * You should use a wrapping component like `ImgUploader` to maintain state and preview the state
 * inside the dropzone/
 *
 * @note this component must be wrapped in a `CameraUploadContext`
 */
const FileUploader = ({ acceptedFileTypes, onFilesUploaded, uploadNameSpace, disabled, disabledMessage, maxFiles = Infinity, maxFileSize = 1e7, message, children, useGuidForFileName }) => {
    const context = (0, react_1.useContext)(CameraUploadContext_1.CameraUploadContext);
    const onDrop = async (acceptedFiles) => {
        // console.log(1, "Rejected Files", rejectedFiles);
        /**
         * If you have a limit of 3 files, and 2 files already exist, only 1 file can be uploaded.
         */
        const numberOfFilesLeft = maxFiles;
        /**
         * Only get the files that we know we should be uploading
         *
         * Also account for a negative numberOfFilesLeft (which shouldn't occur, unless too many existing files have been submitted)
         */
        const validatedFiles = numberOfFilesLeft >= 0 ? acceptedFiles.slice(0, numberOfFilesLeft) : [];
        const uploadedFiles = await Promise.all(validatedFiles.map(async (file) => {
            const type = _.last(file.name.split("."));
            const details = await context.getUploadDetails(uploadNameSpace, useGuidForFileName ? `${(0, guid_1.createGuid)()}.${type}` : file.name);
            // upload
            await (0, axios_1.default)({
                method: details.method,
                url: details.url,
                headers: details.headers,
                data: file
            });
            const url = await context.getSignedImageUrl(details.fileKey);
            return {
                url,
                fileKey: details.fileKey
            };
        }));
        onFilesUploaded(uploadedFiles);
        /**
         * If the file upload limit was reached, lets do something with the files over length to alert the user.
         */
        if (validatedFiles.length !== acceptedFiles.length) {
            const filesOverLimit = acceptedFiles.slice(numberOfFilesLeft);
            alert(`The following files were not uploaded as the max file limit of ${maxFiles} was hit.\n${filesOverLimit.map(x => "\n" + x.name)}`);
        }
    };
    const isDisabled = disabled;
    const { getRootProps, getInputProps } = (0, react_dropzone_1.useDropzone)({
        onDrop,
        disabled: isDisabled,
        accept: acceptedFileTypes,
        maxSize: maxFileSize,
        onDropRejected(files) {
            const fileNames = files.map(f => f.name);
            // warn the user that they need to reduce the file size
            alert(`The files below are over the ${maxFileSize /
                1000000} Mb file size limit:\n${fileNames.join(", ")}\n\nPlease reduce their size and try re-uploading.`);
        }
    });
    return (React.createElement("div", Object.assign({ "data-test": "file-uploader", className: css("dropzone", isDisabled ? "disabled" : "") }, getRootProps()),
        React.createElement("input", Object.assign({}, getInputProps())),
        React.createElement("div", { className: css("dropzone_internal") }, children ? (children) : (React.createElement("div", { className: css("dropzone_internal_no_content") }, isDisabled ? (React.createElement("div", null, disabledMessage || "Uploading is disabled")) : (React.createElement(React.Fragment, null,
            React.createElement("div", null, message || "Drag files here to upload"),
            React.createElement("br", null),
            React.createElement(Button_1.Button, null, "Upload"))))))));
};
exports.FileUploader = FileUploader;
//# sourceMappingURL=index.js.map